import type { HexMapState } from '../../engine/render/renderer';
import type { ContentBuilder } from '../ui/ContentBuilder';
import { AbstractModal } from '../AbstractModal';
import { t } from '../../i18n/translations';
import {
    ACTION__MODAL__TAB__TILE__RAW,
    ACTION__MODAL__TAB__TILE__ENEMIES,
    ACTION__MODAL__TAB__TILE__SETTLEMENT,
    ACTION__MODAL__TAB__TILE__SUMMARY,
    ACTION__TILE__ASSIGN_SCOUT_TARGET,
    ACTION__TILE__OPEN_FOUNDING,
    ACTION__TILE__OPEN_SCOUT,
    ACTION__TILE__RETURN_TROOPS,
} from '../../engine/input/actionIds';
import { renderTileSummaryTab } from './tabs/TabSummary';
import { renderTileSettlementTab } from './tabs/TabSettlement';
import { renderTileRawTab } from './tabs/TabRaw';
import { renderTileEnemiesTab } from './tabs/TabEnemies';
import { ScoutingModal } from '../scouting/ScoutingModal';
import { startReturnMission } from '../../engine/scouting/scouting';
import { FoundingModal } from '../founding/FoundingModal';

export class TileModal extends AbstractModal {
    getHeaderLabel(state: HexMapState): string {
        return t(state.config.ui.language, 'tile.title');
    }

    handleAction(actionId: string, state: HexMapState): boolean {
        switch (actionId) {
            case ACTION__MODAL__TAB__TILE__SUMMARY:
            case ACTION__MODAL__TAB__TILE__SETTLEMENT:
            case ACTION__MODAL__TAB__TILE__RAW:
            case ACTION__MODAL__TAB__TILE__ENEMIES:
                this.activeTabId = actionId;
                this.scroll = 0;
                return true;
            default:
                break;
        }

        if (actionId.startsWith(`${ACTION__TILE__OPEN_SCOUT}:`)) {
            const settlementId = actionId.slice(`${ACTION__TILE__OPEN_SCOUT}:`.length);
            const rect = {
                x: this.rect.x + 20,
                y: this.rect.y + 20,
                w: this.rect.w,
                h: this.rect.h,
            };
            state.modals.push(new ScoutingModal(rect, this.tile, settlementId));
            state.activeModalId = `scout_modal_${this.tile.id}`;
            return true;
        }
        if (actionId === ACTION__TILE__OPEN_FOUNDING) {
            const rect = {
                x: this.rect.x + 20,
                y: this.rect.y + 20,
                w: this.rect.w,
                h: this.rect.h,
            };
            state.modals.push(new FoundingModal(rect, this.tile));
            state.activeModalId = `founding_modal_${this.tile.id}`;
            return true;
        }
        if (actionId.startsWith(`${ACTION__TILE__RETURN_TROOPS}:`)) {
            const missionId = actionId.slice(`${ACTION__TILE__RETURN_TROOPS}:`.length);
            const mission = state.scoutingMissions.find((entry) => entry.id === missionId);
            if (!mission) return false;
            return startReturnMission(mission, state.config.scouting);
        }
        if (actionId.startsWith(`${ACTION__TILE__ASSIGN_SCOUT_TARGET}:`)) {
            const missionId = actionId.slice(`${ACTION__TILE__ASSIGN_SCOUT_TARGET}:`.length);
            const mission = state.scoutingMissions.find((entry) => entry.id === missionId);
            if (!mission || mission.status === 'complete') return false;
            if (mission.targetTileId === this.tile.id) return false;
            mission.plan = [this.tile.id];
            state.scoutPlanningMissionId = null;
            return true;
        }

        return false;
    }

    buildContent(state: HexMapState, builder: ContentBuilder): void {
        const lang = state.config.ui.language;
        if (!this.activeTabId) this.activeTabId = ACTION__MODAL__TAB__TILE__SUMMARY;

        builder.tabs(
            [
                {
                    id: ACTION__MODAL__TAB__TILE__SUMMARY,
                    label: t(lang, 'tile.tabSummary'),
                    active: this.activeTabId === ACTION__MODAL__TAB__TILE__SUMMARY,
                },
                {
                    id: ACTION__MODAL__TAB__TILE__ENEMIES,
                    label: t(lang, 'tile.tabEnemies'),
                    active: this.activeTabId === ACTION__MODAL__TAB__TILE__ENEMIES,
                },
                {
                    id: ACTION__MODAL__TAB__TILE__SETTLEMENT,
                    label: t(lang, 'tile.tabSettlement'),
                    active: this.activeTabId === ACTION__MODAL__TAB__TILE__SETTLEMENT,
                },
                {
                    id: ACTION__MODAL__TAB__TILE__RAW,
                    label: t(lang, 'tile.tabRaw'),
                    active: this.activeTabId === ACTION__MODAL__TAB__TILE__RAW,
                },
            ],
            this.tabScroll,
        );
        builder.spacer(6);

        if (this.activeTabId === ACTION__MODAL__TAB__TILE__SUMMARY) {
            renderTileSummaryTab(builder, state, this.tile, lang);
        }

        if (this.activeTabId === ACTION__MODAL__TAB__TILE__ENEMIES) {
            renderTileEnemiesTab(builder, state, this.tile);
        }

        if (this.activeTabId === ACTION__MODAL__TAB__TILE__SETTLEMENT) {
            renderTileSettlementTab(builder, state, this.tile);
        }

        if (this.activeTabId === ACTION__MODAL__TAB__TILE__RAW) {
            renderTileRawTab(builder, this.tile, lang);
        }
    }
}
